import { filterBranchLocations, hubRecordId, normalizeDestinationHubIds } from './destinationType.js';

/** Normalize hub.dispatch_types to uppercase OGCL/ITCL list (default OGCL). */
export function normalizeHubDispatchTypes(hubOrTypes) {
  const raw = hubOrTypes && typeof hubOrTypes === 'object' && !Array.isArray(hubOrTypes)
    ? hubOrTypes.dispatch_types
    : hubOrTypes;
  if (raw == null || raw === '') return ['OGCL'];
  const list = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
  const normalized = [];
  for (const item of list) {
    const type = String(item || '').trim().toUpperCase();
    if ((type === 'OGCL' || type === 'ITCL') && !normalized.includes(type)) {
      normalized.push(type);
    }
  }
  return normalized.length ? normalized : ['OGCL'];
}

/** Union of dispatch types for selected From hub ids. */
export function unionDispatchTypesForHubIds(hubs, hubIds) {
  const selected = new Set((hubIds || []).map(String));
  const types = new Set();
  for (const hub of hubs || []) {
    const id = hub?.id ?? hub?.hub_id;
    if (!selected.has(String(id))) continue;
    for (const type of normalizeHubDispatchTypes(hub)) {
      types.add(type);
    }
  }
  return types;
}

/**
 * Filter locations whose dispatch types intersect the source set.
 * ITCL-only From → locations that include ITCL (not OGCL-only).
 * OGCL destinations without ITCL require an OGCL Transfer Request.
 */
export function filterHubsByDispatchIntersection(hubs, sourceDispatchTypes) {
  const source = sourceDispatchTypes instanceof Set
    ? sourceDispatchTypes
    : new Set(
        (Array.isArray(sourceDispatchTypes) ? sourceDispatchTypes : [])
          .map((t) => String(t).trim().toUpperCase())
          .filter(Boolean),
      );
  if (!source.size) return Array.isArray(hubs) ? hubs : [];
  return (hubs || []).filter((hub) =>
    normalizeHubDispatchTypes(hub).some((type) => source.has(type)),
  );
}

function destinationAllowListForFromHubs(hubs, fromLocationIds) {
  const selected = new Set((fromLocationIds || []).map(String));
  if (!selected.size) return { unrestricted: true, allowed: new Set() };
  const allowed = new Set();
  let unrestricted = false;
  let sawFromHub = false;
  for (const hub of hubs || []) {
    const id = hubRecordId(hub);
    if (!selected.has(id)) continue;
    sawFromHub = true;
    const ids = normalizeDestinationHubIds(hub.destination_location_hub_ids);
    if (ids == null) {
      unrestricted = true;
      continue;
    }
    ids.forEach((item) => allowed.add(item));
  }
  if (!sawFromHub || unrestricted) {
    return { unrestricted: true, allowed };
  }
  return { unrestricted: false, allowed };
}

/**
 * ML "To" locations for selected From hubs.
 * A configured destination list on the From hub is the To list, including
 * hubs whose dispatch type differs from the source (ITCL branch → OGCL cities).
 * Unrestricted From hubs still use branch destinations, then the dispatch-type filter.
 */
export function toLocationsForMlDispatch(hubs, fromLocationIds, { enabled = true } = {}) {
  const fromSet = new Set((fromLocationIds || []).map(String));
  const { unrestricted, allowed } = destinationAllowListForFromHubs(hubs, fromLocationIds);
  const usingConfiguredList = fromSet.size > 0 && !unrestricted;
  let pool;
  if (usingConfiguredList) {
    pool = (hubs || []).filter((hub) => allowed.has(hubRecordId(hub)));
  } else {
    pool = filterBranchLocations(hubs);
  }
  pool = pool.filter((hub) => !fromSet.has(hubRecordId(hub)));
  if (!enabled || !fromLocationIds?.length || usingConfiguredList) {
    return pool;
  }
  const sourceTypes = unionDispatchTypesForHubIds(hubs, fromLocationIds);
  return filterHubsByDispatchIntersection(pool, sourceTypes);
}
